// Spec iter122 (Feb 2026) — Homogénéisation des badges de statut Tâches.
// Style unique : icône colorée + texte même couleur, sans bordure ni fond,
// font-weight 600, texte 13 px, icône 15 px. « En attente » a un texte
// légèrement plus clair (#c8c8cc) que son icône (#8a8a8f) pour rester
// lisible sur fond sombre.
//
// Alias `Lock as LockIcon` pour éviter la collision avec le global
// navigateur `window.Lock` après minification (fix iter113).
import { AlertTriangle, Play, Check, X, Hourglass, Eye, RotateCw, Lock as LockIcon, Calendar, CheckCircle2, BellRing } from "lucide-react";
import { initialsOf } from "@/components/common/UserInitials";
import DeskTip from "@/components/common/DeskTip";
import UserAvatar from "@/components/ui/UserAvatar";

/*
 * StatusBadge — spec iter122.
 * 7 statuts métier visibles + planned/completed/redo conservés pour compat.
 *
 *   planned     — Planifiée      — #38BDF8 (sky, calendar)
 *   waiting     — En attente     — icône #8a8a8f, texte #c8c8cc, Hourglass, blink
 *   accepted    — Acceptée       — #5aa8e0, Check
 *   in_progress — En cours       — #F97316, Play
 *   to_verify   — À vérifier     — #A855F7, Eye, blink
 *   redo        — À refaire      — #F97316, RotateCw, blink (compat legacy)
 *   completed   — Terminée       — #22C55E, CheckCircle2 (compat legacy)
 *   closed      — Clôturée       — #22C55E, Lock
 *   refused     — Refusée        — #EF4444, X
 *   late        — En retard      — #e07a7a, AlertTriangle, blink
 */
const MAP = {
  planned:     { label: "Planifiée",   color: "#38BDF8", Icon: Calendar,     blink: false },
  waiting:     { label: "En attente",  color: "#8a8a8f", textColor: "#c8c8cc", Icon: Hourglass, blink: true },
  accepted:    { label: "Acceptée",    color: "#5aa8e0", Icon: Check,        blink: false },
  in_progress: { label: "En cours",    color: "#F97316", Icon: Play,         blink: false },
  to_verify:   { label: "À vérifier",  color: "#A855F7", Icon: Eye,          blink: true  },
  redo:        { label: "À refaire",   color: "#F97316", Icon: RotateCw,     blink: true  },
  completed:   { label: "Terminée",    color: "#22C55E", Icon: CheckCircle2, blink: false },
  closed:      { label: "Clôturée",    color: "#22C55E", Icon: LockIcon,     blink: false },
  refused:     { label: "Refusée",     color: "#EF4444", Icon: X,            blink: false },
  late:        { label: "En retard",   color: "#e07a7a", Icon: AlertTriangle, blink: true },
  relance:     { label: "Relancé",     color: "#E0569B", Icon: BellRing,     blink: true  }, // iter402 — indicateur superposé (statut réel inchangé)
};

export default function StatusBadge({ status, size = "sm", iconOnly = false, verifierUser = null, verifiers = null, rowBg = "#1B1B1B", title }) {
  const s = MAP[status] || MAP.waiting;
  // Iter126 pt.1 — « À vérifier » : pile des avatars de TOUS les vérificateurs
  // possibles (Admin / Premier Admin actifs), 22 px, chevauchement −7 px,
  // max 3 + pastille « +N », infobulle applicative = nom complet.
  const list = status === "to_verify" ? (verifiers && verifiers.length ? verifiers : (verifierUser ? [verifierUser] : [])) : [];
  const textColor = s.textColor || s.color;
  const iconSize = size === "lg" ? 18 : 15;
  const fontSize = size === "lg" ? 15 : 13;
  const badge = (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap ${s.blink ? "animate-pulse" : ""}`}
      style={{ color: textColor, fontWeight: 600, fontSize, lineHeight: 1.1 }}
      title={title}
      data-testid={`status-badge-${status}`}
    >
      <s.Icon size={iconSize} strokeWidth={2.2} style={{ color: s.color }} />
      {!iconOnly && <span>{s.label}</span>}
    </span>
  );
  if (!list.length) return badge;
  const shown = list.slice(0, 3);
  const extra = list.length - shown.length;
  const nameOf = (u) => (`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "?");
  // iter436 pt 2 — initiales : source unique (initialsOf)
  return (
    <span className="inline-flex items-center align-middle">
      {badge}
      <span aria-hidden="true" data-testid="to-verify-separator" style={{ display: "inline-block", width: 1, height: 16, background: "#3a3a40", marginLeft: 8, marginRight: 8, flexShrink: 0 }} />
      <span className="inline-flex items-center" data-testid="to-verify-verifiers">
        {shown.map((u, i) => (
          <DeskTip key={u.id || u.email} text={nameOf(u)} side="top" testId={`to-verify-avatar-tip-${u.id}`}>
            <span className="inline-flex rounded-full shrink-0" style={{ marginLeft: i === 0 ? 0 : -7, zIndex: 3 - i, position: "relative", border: `1.5px solid ${rowBg}` }} data-testid="to-verify-avatar">
              <UserAvatar user={u} size={20} showPopover={false} />
            </span>
          </DeskTip>
        ))}
        {extra > 0 && (
          <DeskTip text={list.slice(3).map(nameOf).join(", ")} side="top" testId="to-verify-more-tip">
            <span className="inline-flex items-center justify-center rounded-full shrink-0 text-[9px] font-semibold tabular-nums"
              style={{ width: 22, height: 22, marginLeft: -7, background: "#1c1c20", color: "#8b8b93", border: `1.5px solid ${rowBg}`, position: "relative" }} data-testid="to-verify-more">+{extra}</span>
          </DeskTip>
        )}
      </span>
    </span>
  );
}

export const STATUS_LABEL = Object.fromEntries(Object.entries(MAP).map(([k, v]) => [k, v.label]));
export const STATUS_COLOR = Object.fromEntries(Object.entries(MAP).map(([k, v]) => [k, v.color]));
